import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { X, Play } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";
import { useProfileSections } from "@/lib/hooks/profile-sections/use-profile-sections";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { CardHeader, CardTitle } from "../ui/card";
import { InterestsContent } from "../conversation-details/side-bar/sections/interests-content";
import { MutingVideo } from "@/components/shared/muting-media";

interface MentorData {
  name: string;
  bio: string;
  pronouns?: string;
  age?: string;
  interests?: string[];
  favoriteSong?: string;
  favoriteFood?: string;
  videoUrl?: string;
  headshotUrl?: string;
  life_experiences?: string[];
  userId?: string; // Add userId to fetch custom sections
}

interface MentorDetailInfoProps {
  mentor: MentorData;
}

export function MentorDetailInfo({ mentor }: MentorDetailInfoProps) {
  const [isBioOpen, setIsBioOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Fetch custom profile sections if userId is available
  const { data: profileSections = [] } = useProfileSections(
    mentor.userId || "",
  );

  useEffect(() => {
    if (videoRef.current) {
      const handleVideoEnd = () => setIsVideoOpen(false);
      videoRef.current.addEventListener("ended", handleVideoEnd);
      return () =>
        videoRef.current?.removeEventListener("ended", handleVideoEnd);
    }
  }, []);

  return (
    <div className="relative flex w-full flex-col">
      

      <div className="flex flex-col py-3">

        <div className="space-y-6 px-4">
          {/* Basic Info */}
          <CardHeader className="flex flex-col items-center gap-4 pb-2">
                  <Avatar className="h-32 w-32">
                    <AvatarImage src={mentor?.headshotUrl} alt={mentor.name} />
                    <AvatarFallback>{getInitials(mentor.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-base">{mentor.name}</CardTitle>
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {mentor.pronouns && (
                        <span className="bg-muted rounded-md px-2 py-1.5 text-sm font-medium">
                          {mentor.pronouns}
                        </span>
                      )}
                      {mentor.age && (
                        <span className="bg-muted rounded-md px-2 py-1.5 text-sm font-medium">
                          {mentor.age} yrs old
                        </span>
                      )}
                    </div>
                  </div>
          </CardHeader>
          
          {/* Bio */}
          <div className="space-y-2">
            <p className={cn("text-base", !isBioOpen && "line-clamp-2")}>
              {mentor.bio}
            </p>
            <button
              className="font-medium hover:underline"
              onClick={() => setIsBioOpen(!isBioOpen)}
            >
              {isBioOpen ? "Read less" : "Read more"}
            </button>
          </div>
          {/* Interests Section */}
          {mentor.interests && mentor.interests.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">Interests</span>
              </div>
              <div className="flex flex-col gap-2">
                <InterestsContent data={mentor.interests as any}/> 
              </div>
            </div>
          )}

          

          {/* Favorites Section */}
          {(mentor.favoriteSong) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">My Favorite Song</span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {mentor.favoriteSong && (
                  <div className="flex h-36 min-w-[288px] flex-col items-center justify-center gap-4 rounded-xl border bg-primary/5 p-6">
                    <span className="font-medium">Music</span>
                    <iframe src={mentor.favoriteSong} width="550" height="80" />
                  </div>
                )}
                
              </div>
            </div>
          )}

          
          {/* Custom Profile Sections */}
          {profileSections.length > 0 && (
            <div className="space-y-6">
              {profileSections.map((section) => (
                <CustomProfileSection key={section.id} section={section} />
              ))}
            </div>
          )}
          {/* Video Thumbnail */}
        {mentor.videoUrl && (
          <div
            className="group relative cursor-pointer"
            onClick={() => {
              setIsVideoOpen(true);
              videoRef.current?.play();
            }}
          >
            <span className="text-lg font-bold">Meet me for real</span>
            <div className="aspect-video w-full overflow-hidden rounded-lg">
              {mentor?.headshotUrl ? (
                <img
                  src={mentor.headshotUrl}
                  alt={mentor.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="bg-muted flex h-full w-full items-center justify-center">
                  <Avatar className="h-32 w-32">
                    <AvatarFallback>{getInitials(mentor.name)}</AvatarFallback>
                  </Avatar>
                </div>
              )}
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-black/50 p-4 transition-colors group-hover:bg-black/70">
                <Play className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        )}
        {/* Video Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-20 bg-black transition-all",
          isVideoOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <div className="absolute flex h-full w-full items-center justify-center">
          <div className="relative flex w-full max-w-[600px] justify-center">
            {mentor.videoUrl && (
              <MutingVideo
                controls={false}
                playsInline
                preload="auto"
                className="h-full object-cover"
                ref={videoRef}
              >
                <source src={mentor.videoUrl + "#t=0.0001"} />
              </MutingVideo>
            )}
          </div>
        </div>

        <button
          className="absolute right-6 top-6 rounded-lg bg-black/20 p-3 text-white backdrop-blur-md"
          onClick={() => {
            setIsVideoOpen(false);
            videoRef.current?.pause();
          }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
        </div>
      </div>
    </div>
  );
}

// Component to display individual custom profile sections
function CustomProfileSection({
  section,
}: {
  section: {
    id: string;
    title: string;
    description: string;
    image_url?: string;
  };
}) {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { url: signedImageUrl } = useSignedMedia(section.image_url);

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{section.title}</h3>

      {signedImageUrl && (
        <div className="mb-3">
          <img
            src={signedImageUrl}
            alt={section.title}
            className="h-48 w-full max-w-md rounded-lg border object-cover"
          />
        </div>
      )}

      <div className="text-muted-foreground text-sm">
        <p className={showFullDescription ? "" : "line-clamp-3"}>
          {section.description}
        </p>
        {section.description.length > 200 && (
          <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            className="mt-1 text-xs text-primary hover:underline"
          >
            {showFullDescription ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </div>
  );
}
